import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { thunkSetRequestedBuyIn, thunkSitDown } from '../../redux/actions/pokerActions';
import { DEFAULT_REMEMBER_REQUESTED_BUY_IN } from '../../redux/reducers/pokerReducer';
import { selectMyNumChips, selectMyNumJacks } from '../../redux/reducers/selfDataReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { LIGHT_YELLOW_COLOR } from '../../styles/colors';
import { CURRENCY_NAMES } from '../../types/sharedTypes';
import { getDiscreteBuyInAmounts } from '../../utils/buyInUtils';
import { getCurrencyAmountString } from '../../utils/currencyUtils';
import { FormControlContainer } from '../formControlContainer';
import { WarningMessageContainer } from '../warningMessageContainer';
// should never manifest to the user, but ensures that pendingBuyIn is not undefined:
const DEFAULT_PENDING_BUY_IN = 500;
const PENDING_BUY_IN_CONTAINER_CSS = css `
  text-align: center;
`;
const SLIDER_CONTAINER_CSS = css `
  padding: 5px 20px;
`;
const DIALOG_ACTIONS_CONTAINER_CSS = css `
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
`;
/**
 * Create slider marks only at the start and end of the range.
 */
function getSliderMarks(buyInAmounts, currency) {
    return buyInAmounts.map((buyInAmount, i) => ({
        value: buyInAmount,
        label: i === 0 || i === buyInAmounts.length - 1 ? getCurrencyAmountString(buyInAmount, currency, false) : undefined
    }));
}
export const SetBuyInMessageBoxContents = ({ seatIndex, onClose }) => {
    const dispatch = useAppDispatch();
    const buyIn = useAppSelector((state) => state.poker.buyIn);
    const minBuyIn = useAppSelector((state) => state.poker.minBuyIn);
    const maxBuyIn = useAppSelector((state) => state.poker.maxBuyIn);
    const requestedBuyIn = useAppSelector((state) => state.poker.requestedBuyIn);
    const rememberRequestedBuyIn = useAppSelector((state) => state.poker.rememberRequestedBuyIn);
    const currency = useAppSelector((state) => state.poker.currency);
    const myNumChips = useAppSelector(selectMyNumChips);
    const myNumJacks = useAppSelector(selectMyNumJacks);
    const availableCurrencyAmount = currency === 'jacks' ? myNumJacks : myNumChips;
    const socketSend = useSocketSend();
    const [pendingBuyIn, setPendingBuyIn] = useState(requestedBuyIn ? requestedBuyIn : buyIn || DEFAULT_PENDING_BUY_IN);
    const [rememberRequestedBuyInOnControl, setRememberRequestedBuyInOnControl] = useState(DEFAULT_REMEMBER_REQUESTED_BUY_IN);
    useEffect(() => {
        if (requestedBuyIn) {
            setPendingBuyIn(requestedBuyIn);
        }
    }, [requestedBuyIn]);
    useEffect(() => {
        setRememberRequestedBuyInOnControl(rememberRequestedBuyIn);
    }, [rememberRequestedBuyIn]);
    const onChangeSlider = useCallback((event, value) => {
        if (Array.isArray(value)) {
            return;
        }
        setPendingBuyIn(value);
    }, []);
    const onChangeRememberRequestedBuyIn = useCallback((event) => {
        setRememberRequestedBuyInOnControl(event.target.checked);
    }, []);
    const onClickSetBuyIn = useCallback(() => {
        onClose();
        if (!buyIn || !minBuyIn || !maxBuyIn) {
            return;
        }
        if (seatIndex !== undefined) {
            dispatch(thunkSitDown(seatIndex, pendingBuyIn, rememberRequestedBuyInOnControl, socketSend));
        }
        else {
            dispatch(thunkSetRequestedBuyIn(pendingBuyIn, rememberRequestedBuyInOnControl, socketSend));
        }
    }, [
        buyIn,
        pendingBuyIn,
        dispatch,
        maxBuyIn,
        minBuyIn,
        onClose,
        rememberRequestedBuyInOnControl,
        seatIndex,
        socketSend
    ]);
    if (!buyIn || !minBuyIn || !maxBuyIn || availableCurrencyAmount === undefined || !currency) {
        return null;
    }
    let buyInAmounts = getDiscreteBuyInAmounts(buyIn, minBuyIn, maxBuyIn, availableCurrencyAmount);
    let sliderElement;
    let buttonElement;
    if (!buyInAmounts) {
        // Happens if there is a buy-in range, but the player doesn't even have the minimum.
        // Show the full range in the slider, but disabled.
        // Recalculate the full range (without checking availableCurrencyAmount):
        buyInAmounts = getDiscreteBuyInAmounts(buyIn, minBuyIn, maxBuyIn);
        if (buyInAmounts) {
            sliderElement = (_jsx(Slider, { onChange: onChangeSlider, value: buyIn, min: minBuyIn, max: maxBuyIn, marks: getSliderMarks(buyInAmounts, currency), step: null, disabled: true }));
        }
        buttonElement = (_jsxs(Button, { variant: 'outlined', disabled: true, children: ["Not Enough ", CURRENCY_NAMES[currency]] }));
    }
    else {
        const sliderMaxValue = Math.min(maxBuyIn, availableCurrencyAmount);
        sliderElement = (_jsx(Slider, { onChange: onChangeSlider, value: pendingBuyIn, min: minBuyIn, max: sliderMaxValue, marks: getSliderMarks(buyInAmounts, currency), step: null }));
        buttonElement = (_jsx(Button, { "aria-label": 'Set Buy-in', variant: 'contained', onClick: onClickSetBuyIn, size: 'large', children: seatIndex !== undefined ? 'Sit' : 'Set Buy-in' }));
    }
    return (_jsxs(_Fragment, { children: [currency === 'jacks' && _jsx(WarningMessageContainer, { message: 'This game plays with Jacks (not Chips).' }), _jsxs(FormControlContainer, { children: [_jsx("div", { css: PENDING_BUY_IN_CONTAINER_CSS, children: _jsxs(Typography, { "aria-label": 'Buy-in Amount', variant: 'h6', children: ["Buy-in:", ' ', _jsx("span", { css: css `
                color: ${LIGHT_YELLOW_COLOR};
              `, children: getCurrencyAmountString(pendingBuyIn, currency) }), "\u00A0*"] }) }), _jsx("div", { css: SLIDER_CONTAINER_CSS, children: sliderElement })] }), _jsx(DialogContentText, { children: myNumChips !== undefined && myNumJacks !== undefined && (_jsxs(Typography, { children: ["You have: ", getCurrencyAmountString(currency === 'chips' ? myNumChips : myNumJacks, currency)] })) }), _jsx(DialogContentText, { children: _jsx(Typography, { variant: 'subtitle2', css: css `
            opacity: 0.5;
          `, children: "*\u00A0Or your last chip stack, whichever is more." }) }), _jsxs("div", { css: DIALOG_ACTIONS_CONTAINER_CSS, children: [_jsx(FormControl, { component: 'fieldset', children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: rememberRequestedBuyInOnControl, onChange: onChangeRememberRequestedBuyIn }), label: 'Remember Buy-in' }) }), buttonElement] })] }));
};
